import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, PageContainer } from "@fleet/ui";
import { InviteMemberDialog } from "@/features/organizations/components/invite-member-dialog";
import { ChangeRoleDialog } from "@/features/organizations/components/change-role-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { AlertTriangle, Hammer, Shield, UserPlus } from "@fleet/icons";

const SIMULATED_DELAY = 1000;

function ModalSandboxPage() {
  const { t } = useI18n();
  const [inviteOpen, setInviteOpen] = createSignal(false);
  const [roleOpen, setRoleOpen] = createSignal(false);
  const [confirmOpen, setConfirmOpen] = createSignal(false);

  const handleInvite = async () => {
    await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));
    showToast(t(msg`Invitation sent (simulated)`), "success");
  };

  const handleRoleChange = async () => {
    await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));
    showToast(t(msg`Role updated (simulated)`), "success");
  };

  return (
    <div class="flex flex-1 flex-col min-h-0 overflow-hidden bg-background">
      <PageContainer maxWidth="4xl" class="space-y-8" containerClass="min-h-0">
        {/* Header */}
        <div>
          <h1 class="text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Hammer class="h-8 w-8 text-primary" />
            {t(msg`Modal Sandbox`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Interactive testing environment for validating user experience and transitions of all application modals.`,
            )}
          </p>
        </div>

        {/* Modals Testing Cards Grid */}
        <div class="grid gap-6 sm:grid-cols-2">
          {/* Invite Member Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <UserPlus class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Invite Member Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Invite a member to an organization`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setInviteOpen(true)} class="w-full">
                {t(msg`Open Invite Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Change Role Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Shield class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Change Role Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`Change a member role`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setRoleOpen(true)}
                class="w-full bg-emerald-600 hover:bg-emerald-500 text-white border-none"
              >
                {t(msg`Open Role Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Confirm Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive">
                <AlertTriangle class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Confirm Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Generic destructive confirmation`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={() => setConfirmOpen(true)} class="w-full">
                {t(msg`Open Confirm Dialog`)}
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>

      <InviteMemberDialog
        open={inviteOpen()}
        onOpenChange={setInviteOpen}
        onInvite={handleInvite}
      />

      <ChangeRoleDialog
        open={roleOpen()}
        onOpenChange={setRoleOpen}
        memberName={t(msg`Sandbox Member`)}
        currentRole="member"
        onRoleChange={handleRoleChange}
      />

      <ConfirmDialog
        open={confirmOpen()}
        onOpenChange={setConfirmOpen}
        title={t(msg`Delete item`)}
        description={t(msg`Are you sure? This action cannot be undone.`)}
        onConfirm={() => {
          showToast(t(msg`Confirmed (simulated)`), "success");
        }}
      />
    </div>
  );
}

export const sandboxModalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/modal",
  component: ModalSandboxPage,
});
